// src/components/OrdersTable.js
import { useMemo, useState, useEffect, useRef } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";

function toNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function formatBRL(value, digits = 2) {
  const n = toNumber(value, 0);
  return n.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

function formatDateTime(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString("pt-BR");
}

function formatSignedBRL(value, digits = 2) {
  const n = toNumber(value, 0);
  if (n < 0) return `- ${formatBRL(Math.abs(n), digits)}`;
  return formatBRL(n, digits);
}

function formatPercent(value, digits = 2) {
  const n = toNumber(value, 0);
  const abs = Math.abs(n).toFixed(digits);
  return `${n < 0 ? "- " : ""}${abs}%`;
}

function readFirstFinite(obj, keys = []) {
  for (const key of keys) {
    const val = obj?.[key];
    if (val !== null && val !== undefined) {
      const n = Number(val);
      if (Number.isFinite(n)) return n;
    }
  }
  return null;
}

function getCurrentMarketPrice(marketData) {
  return toNumber(
    marketData?.currentPrice ?? marketData?.current_price ?? marketData?.price,
    0
  );
}

/** Valida se um preço parece "preço da saca em BRL". */
function isLikelySacaPrice(price, currentPrice = 0) {
  const p = toNumber(price, 0);
  if (p <= 0) return false;
  if (p < 100 || p > 50000) return false;

  if (currentPrice > 0) {
    const ratio = p / currentPrice;
    if (ratio < 0.1 || ratio > 10) return false;
  }
  return true;
}

function getEntrySacaPrice(order, currentPrice = 0) {
  const explicit = readFirstFinite(order, [
    "entry_price_saca",
    "saca_entry_price",
    "entry_market_price",
    "market_entry_price",
    "entry_price_brl_saca",
    "open_price_saca",
    "price_entry_saca",
  ]);

  if (isLikelySacaPrice(explicit, currentPrice)) return explicit;

  const genericAlt = readFirstFinite(order, [
    "price",
    "entry",
    "open_price",
    "opening_price",
    "unit_price",
  ]);

  if (isLikelySacaPrice(genericAlt, currentPrice)) return genericAlt;

  const defaultEntry = toNumber(order?.entry_price, 0);
  if (isLikelySacaPrice(defaultEntry, currentPrice)) return defaultEntry;

  const explicitSacaQty = readFirstFinite(order, [
    "saca_qty",
    "qty_saca",
    "quantity_saca",
    "market_qty",
    "commodity_qty",
    "asset_qty_saca",
  ]);

  const amount = toNumber(order?.amount, 0);
  if (amount > 0 && explicitSacaQty && explicitSacaQty > 0) {
    return amount / explicitSacaQty;
  }

  if (currentPrice > 0) return currentPrice;
  return 0;
}

function getOrderQtySaca(order, entrySacaPrice, currentPrice = 0) {
  const explicitSacaQty = readFirstFinite(order, [
    "saca_qty",
    "qty_saca",
    "quantity_saca",
    "market_qty",
    "commodity_qty",
    "asset_qty_saca",
  ]);
  if (explicitSacaQty && explicitSacaQty > 0) return explicitSacaQty;

  const genericQty = readFirstFinite(order, ["qty", "quantity", "asset_qty", "position_qty"]);

  const amount = toNumber(order?.amount, 0);
  const basisPrice = entrySacaPrice > 0 ? entrySacaPrice : currentPrice;

  if (genericQty && genericQty > 0) {
    if (amount > 0 && basisPrice > 0) {
      const implied = genericQty * basisPrice;
      const ratio = implied / amount;
      if (ratio >= 0.2 && ratio <= 5) return genericQty;
    } else {
      return genericQty;
    }
  }

  if (amount > 0 && entrySacaPrice > 0) return amount / entrySacaPrice;
  return 0;
}

function calcOpenPL(order, currentPrice, entrySacaPrice, qtySaca) {
  const side = String(order?.side || "").toLowerCase();
  if (entrySacaPrice <= 0 || qtySaca <= 0 || currentPrice <= 0) return 0;

  if (side === "buy") return qtySaca * (currentPrice - entrySacaPrice);
  return qtySaca * (entrySacaPrice - currentPrice);
}

function getSideLabel(side) {
  const s = String(side || "").toLowerCase();
  if (s === "buy") return "COMPRA";
  if (s === "sell") return "VENDA";
  return String(side || "-").toUpperCase();
}

function getSideColor(side) {
  const s = String(side || "").toLowerCase();
  if (s === "buy") return "text-emerald-300";
  if (s === "sell") return "text-cyan-300";
  return "text-slate-300";
}

function getStatusBadge(status) {
  const s = String(status || "").toLowerCase();

  if (s === "open") return "bg-emerald-500/15 text-emerald-300 border-emerald-500/30";
  if (s === "closed") return "bg-slate-500/15 text-slate-200 border-slate-500/30";
  if (s === "canceled") return "bg-amber-500/15 text-amber-300 border-amber-500/30";

  return "bg-slate-500/15 text-slate-200 border-slate-500/30";
}

function minCloseRemaining(order, userSettings) {
  const minMinutes = toNumber(userSettings?.min_close_time_minutes, 0);
  if (minMinutes <= 0) return 0;

  const createdAt = order?.created_at ? new Date(order.created_at) : null;
  if (!createdAt || Number.isNaN(createdAt.getTime())) return 0;

  const now = new Date();
  const elapsedMs = now.getTime() - createdAt.getTime();
  const minMs = minMinutes * 60 * 1000;
  const leftMs = minMs - elapsedMs;

  return leftMs > 0 ? leftMs : 0;
}

function formatRemaining(ms) {
  if (!ms || ms <= 0) return "0m";
  const totalMinutes = Math.ceil(ms / 60000);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

export default function OrdersTable({
  orders = [],
  marketData = null,
  marketPrices = {},
  onOrderClosed,
  userSettings = {},
}) {
  const accountMode = (() => {
    try {
      return localStorage.getItem("cofy_trader_account_mode") === "demo" ? "demo" : "real";
    } catch (_) {
      return "real";
    }
  })();
  const [expandedId, setExpandedId] = useState(null);
  const [closingId, setClosingId] = useState(null);
  const [confirmCloseId, setConfirmCloseId] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [protectionId, setProtectionId] = useState(null);
  const [protectionSL, setProtectionSL] = useState("");
  const [protectionTP, setProtectionTP] = useState("");
  const [savingProtection, setSavingProtection] = useState(false);

  // ✅ usa a base que o TradingDashboard detectou
  const [apiBase, setApiBase] = useState(
    localStorage.getItem("api_base_used") || `${window.location.origin}/api/trader`
  );

  useEffect(() => {
    const sync = () => {
      const v = localStorage.getItem("api_base_used");
      if (v && v !== apiBase) setApiBase(v);
    };
    sync();
    window.addEventListener("storage", sync);
    const t = setInterval(sync, 1500);
    return () => {
      window.removeEventListener("storage", sync);
      clearInterval(t);
    };
  }, [apiBase]);

  const fallbackCurrentPrice = useMemo(() => getCurrentMarketPrice(marketData), [marketData]);
  const arabicaCurrentPrice = toNumber(marketPrices?.CAFE_ARABICA, 0);
  const conilonCurrentPrice = toNumber(marketPrices?.CAFE_CONILON, 0);
  const getOrderCurrentPrice = (order) => {
    const instrument = String(order?.instrument || "CAFE_ARABICA").toUpperCase();
    if (instrument === "CAFE_CONILON") return conilonCurrentPrice > 0 ? conilonCurrentPrice : fallbackCurrentPrice;
    return arabicaCurrentPrice > 0 ? arabicaCurrentPrice : fallbackCurrentPrice;
  };
  const list = Array.isArray(orders) ? orders : [];

  // ✅ Só precisa de feed se existir ordem ABERTA
  const needsLiveFeed = useMemo(
    () => list.some((o) => String(o?.status || "").toLowerCase() === "open"),
    [list]
  );

  // ===== FEED STATUS =====
  const zeroSinceRef = useRef(null);
  const FEED_GRACE_MS = 6000;

  const feedState = useMemo(() => {
    if (!needsLiveFeed) return "off"; // ✅ não mostra aviso em "Fechadas"
    const openList = list.filter((o) => String(o?.status || "").toLowerCase() === "open");
    if (openList.length > 0 && openList.every((o) => getOrderCurrentPrice(o) > 0)) return "ok";

    const now = Date.now();
    if (!zeroSinceRef.current) zeroSinceRef.current = now;
    const ms = now - zeroSinceRef.current;
    return ms < FEED_GRACE_MS ? "connecting" : "down";
  }, [fallbackCurrentPrice, arabicaCurrentPrice, conilonCurrentPrice, needsLiveFeed, list]);

  useEffect(() => {
    const openList = list.filter((o) => String(o?.status || "").toLowerCase() === "open");
    if (openList.length === 0 || openList.every((o) => getOrderCurrentPrice(o) > 0)) zeroSinceRef.current = null;
  }, [fallbackCurrentPrice, arabicaCurrentPrice, conilonCurrentPrice, list]);

  const toggleExpand = (id) => {
    setErrorMsg("");
    setConfirmCloseId((prev) => (prev === id ? null : prev));
    setExpandedId((prev) => (prev === id ? null : id));
  };

  const openProtectionEditor = (order) => {
    setErrorMsg("");
    setProtectionId(Number(order?.id));
    setProtectionSL(order?.stop_loss ?? "");
    setProtectionTP(order?.take_profit ?? "");
  };

  const closeProtectionEditor = () => {
    if (savingProtection) return;
    setProtectionId(null);
    setProtectionSL("");
    setProtectionTP("");
  };

  const saveProtection = async (order) => {
    if (!order?.id) return;

    try {
      setSavingProtection(true);
      setErrorMsg("");

      const protectionUrl =
        accountMode === "demo"
          ? `${apiBase}/demo/orders/${order.id}/protection`
          : `${apiBase}/trading/orders/${order.id}/protection`;

      await axios.post(protectionUrl, {
        stop_loss: String(protectionSL || "").trim() === "" ? null : protectionSL,
        take_profit: String(protectionTP || "").trim() === "" ? null : protectionTP,
      });

      setProtectionId(null);
      if (typeof onOrderClosed === "function") onOrderClosed();
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.error ||
        "Não foi possível salvar Stop Loss / Take Profit.";
      setErrorMsg(msg);
    } finally {
      setSavingProtection(false);
    }
  };

  const handleCloseOrder = async (order) => {
    if (!order?.id) return;

    const remaining = minCloseRemaining(order, userSettings);
    if (remaining > 0) {
      setErrorMsg(`Esta ordem ainda não pode ser encerrada. Tempo restante: ${formatRemaining(remaining)}.`);
      return;
    }

    try {
      setErrorMsg("");
      setClosingId(order.id);

      const closeUrl =
        accountMode === "demo"
          ? `${apiBase}/demo/orders/${order.id}/close`
          : `${apiBase}/trading/orders/${order.id}/close`;

      await axios.post(closeUrl);

      setConfirmCloseId(null);
      if (typeof onOrderClosed === "function") onOrderClosed();
    } catch (err) {
      console.error("Erro ao encerrar ordem:", err);
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.error ||
        "Erro ao encerrar ordem.";
      setErrorMsg(msg);
    } finally {
      setClosingId(null);
    }
  };

  const requestCloseConfirmation = (order) => {
    if (!order?.id) return;

    const remaining = minCloseRemaining(order, userSettings);
    if (remaining > 0) {
      setErrorMsg(`Esta ordem ainda não pode ser encerrada. Tempo restante: ${formatRemaining(remaining)}.`);
      return;
    }

    setErrorMsg("");
    setConfirmCloseId(Number(order.id));
  };

  const cancelCloseConfirmation = () => {
    setConfirmCloseId(null);
  };

  if (!list.length) {
    return (
      <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-4 text-sm text-slate-400">
        Nenhuma operação encontrada.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* ✅ Aviso do feed (só quando precisa) */}
      {feedState === "connecting" && (
        <div className="rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-200">
          Conectando feed de mercado…
        </div>
      )}

      {feedState === "down" && (
        <div className="rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-200">
          Feed de mercado indisponível (preço atual = 0). Confira se o TradingDashboard está passando marketDataForOrders para esta tabela.
        </div>
      )}

      {errorMsg ? (
        <div className="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-300">
          {errorMsg}
        </div>
      ) : null}

      {list.map((order) => {
        const id = Number(order?.id);
        const side = String(order?.side || "").toLowerCase();
        const status = String(order?.status || "").toLowerCase();

        const amount = toNumber(order?.amount, 0);
        const orderInstrument = String(order?.instrument || "CAFE_ARABICA").toUpperCase() === "CAFE_CONILON"
          ? "CAFE_CONILON"
          : "CAFE_ARABICA";
        const orderInstrumentLabel = orderInstrument === "CAFE_CONILON" ? "CAFÉ CONILON" : "CAFÉ ARÁBICA";
        const currentPrice = getOrderCurrentPrice(order);

        const entryPriceSaca = getEntrySacaPrice(order, currentPrice);
        const qtySaca = getOrderQtySaca(order, entryPriceSaca, currentPrice);

        const isOpen = status === "open";
        const isExpanded = expandedId === id;

        // ✅ se for aberta e feed cair: não mostra P/L falso
        const plNowValue =
          isOpen
            ? currentPrice > 0
              ? calcOpenPL(order, currentPrice, entryPriceSaca, qtySaca)
              : null
            : toNumber(order?.profit_loss, 0);

        const plPercent = plNowValue !== null && amount > 0 ? (plNowValue / amount) * 100 : null;

        const basePriceForPosition = currentPrice > 0 ? currentPrice : entryPriceSaca;
        const posCurrentValue = qtySaca > 0 && basePriceForPosition > 0 ? qtySaca * basePriceForPosition : 0;

        const remainingMs = isOpen ? minCloseRemaining(order, userSettings) : 0;
        const canClose = isOpen && remainingMs <= 0;
        const isConfirmingClose = confirmCloseId === id;

        return (
          <div
            key={id}
            className="rounded-xl border border-slate-800 bg-slate-900 shadow-sm overflow-hidden"
          >
            <button
              type="button"
              onClick={() => toggleExpand(id)}
              className="w-full text-left px-4 py-3 hover:bg-slate-800/40 transition-colors"
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold text-white">Ordem #{id}</div>
                  <div className="mt-0.5 text-xs text-slate-300">
                    <span className={orderInstrument === "CAFE_CONILON" ? "font-bold text-amber-300" : "font-bold text-cyan-300"}>
                      {orderInstrumentLabel}
                    </span>
                    {" • "}
                    <span className={`font-semibold ${getSideColor(side)}`}>
                      {getSideLabel(side)}
                    </span>
                  </div>
                </div>

                <span className={`text-[10px] px-2 py-1 rounded-md border ${getStatusBadge(status)}`}>
                  {status === "open" ? "ABERTA" : status === "closed" ? "FECHADA" : status.toUpperCase()}
                </span>
              </div>

              <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                <div className="rounded-lg bg-slate-800/60 p-2">
                  <div className="text-slate-400">Valor investido</div>
                  <div className="font-semibold text-white">{formatBRL(amount)}</div>
                </div>

                <div className="rounded-lg bg-slate-800/60 p-2">
                  <div className="text-slate-400">{isOpen ? "Lucro/Prejuízo atual" : "Resultado"}</div>

                  {plNowValue === null ? (
                    <div className="font-semibold text-slate-300">—</div>
                  ) : (
                    <div className={`font-semibold ${plNowValue >= 0 ? "text-emerald-300" : "text-red-300"}`}>
                      {formatSignedBRL(plNowValue)}{" "}
                      {plPercent !== null && <span className="text-[10px]">({formatPercent(plPercent)})</span>}
                    </div>
                  )}
                </div>
              </div>
            </button>

            {isOpen && (
              <div className="grid grid-cols-2 gap-2 border-t border-slate-800 bg-slate-950/35 px-3 py-2.5 sm:px-4">
                <Button
                  type="button"
                  onClick={() => openProtectionEditor(order)}
                  className="h-10 rounded-xl border border-cyan-500/35 bg-cyan-500/10 text-[10px] font-black text-cyan-200 hover:bg-cyan-500/20"
                >
                  GERENCIAR SL / TARGET
                </Button>

                <Button
                  type="button"
                  onClick={() => toggleExpand(id)}
                  className="h-10 rounded-xl border border-slate-700 bg-slate-800 text-[10px] font-black text-white hover:bg-slate-700"
                >
                  {isExpanded ? "OCULTAR DETALHES" : "VER DETALHES"}
                </Button>
              </div>
            )}

            {isExpanded && (
              <div className="border-t border-slate-800 px-4 py-3 space-y-3">
                <div className="rounded-lg border border-slate-700 bg-slate-800/40 p-3">
                  <div className="text-xs text-slate-400">Sua posição (equivalente)</div>
                  <div className="mt-1 text-sm font-semibold text-white">
                    {qtySaca > 0 ? `${qtySaca.toFixed(4)} saca(s)` : "—"}
                  </div>
                  <div className="text-xs text-slate-300 mt-1">≈ {formatBRL(posCurrentValue)} hoje</div>
                </div>

                <div className="rounded-lg border border-slate-700 bg-slate-800/40 p-3">
                  <div className="text-xs text-slate-400">Preço da saca agora</div>
                  <div className="mt-1 text-sm font-semibold text-white">
                    {currentPrice > 0 ? formatBRL(currentPrice, 4) : "—"}
                  </div>
                </div>

                <div className="space-y-2 text-xs">
                  <div className="flex justify-between gap-3">
                    <span className="text-slate-400">Preço de entrada (saca)</span>
                    <span className="text-white font-medium">
                      {entryPriceSaca > 0 ? formatBRL(entryPriceSaca, 4) : "—"}
                    </span>
                  </div>

                  <div className="flex justify-between gap-3">
                    <span className="text-slate-400">Valor investido</span>
                    <span className="text-white font-medium">{formatBRL(amount)}</span>
                  </div>

                  <div className="flex justify-between gap-3">
                    <span className="text-slate-400">Data de abertura</span>
                    <span className="text-white font-medium">{formatDateTime(order?.created_at)}</span>
                  </div>

                  <div className="flex justify-between gap-3">
                    <span className="text-slate-400">Data de fechamento</span>
                    <span className="text-white font-medium">{formatDateTime(order?.closed_at)}</span>
                  </div>

                  {isOpen && toNumber(userSettings?.min_close_time_minutes, 0) > 0 && (
                    <div className="flex justify-between gap-3">
                      <span className="text-slate-400">Tempo mínimo restante</span>
                      <span className={`font-medium ${remainingMs > 0 ? "text-amber-300" : "text-emerald-300"}`}>
                        {remainingMs > 0 ? formatRemaining(remainingMs) : "Liberado"}
                      </span>
                    </div>
                  )}
                </div>

                {isOpen && (
                  <div className="rounded-xl border border-cyan-400/20 bg-cyan-400/5 p-3">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <div className="text-xs font-black text-white">Stop Loss / Target da posição</div>
                        <div className="mt-1 text-[11px] text-slate-400">
                          Stop Loss: <span className="text-red-300">{order?.stop_loss ? formatBRL(order.stop_loss, 4) : "não definido"}</span>
                          {" • "}
                          Take Profit: <span className="text-emerald-300">{order?.take_profit ? formatBRL(order.take_profit, 4) : "não definido"}</span>
                        </div>
                      </div>
                      <Button
                        type="button"
                        onClick={() => openProtectionEditor(order)}
                        className="shrink-0 bg-cyan-500/15 text-cyan-200 hover:bg-cyan-500/25"
                      >
                        {order?.stop_loss || order?.take_profit ? "ALTERAR" : "ADICIONAR"}
                      </Button>
                    </div>
                  </div>
                )}

                {isOpen && typeof onOrderClosed === "function" && (
                  <div className="pt-1">
                    <div className="mb-2 flex items-center justify-between gap-3">
                      <div>
                        <div className="text-xs font-black text-white">Gerenciar posição</div>
                        <div className="mt-0.5 text-[10px] text-slate-500">
                          Confira os dados antes de encerrar.
                        </div>
                      </div>
                      <span className={`rounded-full border px-2 py-1 text-[9px] font-black ${
                        plNowValue !== null && plNowValue >= 0
                          ? "border-emerald-500/25 bg-emerald-500/10 text-emerald-300"
                          : "border-red-500/25 bg-red-500/10 text-red-300"
                      }`}>
                        {plNowValue === null ? "RESULTADO —" : `${plNowValue >= 0 ? "+" : ""}${formatSignedBRL(plNowValue)}`}
                      </span>
                    </div>

                    {!isConfirmingClose ? (
                      <>
                        <Button
                          type="button"
                          onClick={() => requestCloseConfirmation(order)}
                          disabled={closingId === id || !canClose}
                          className="h-12 w-full rounded-xl border border-red-500/35 bg-red-500/10 text-xs font-black text-red-200 hover:bg-red-500/20"
                        >
                          FECHAR POSIÇÃO
                        </Button>

                        <p className="mt-2 text-center text-[10px] text-slate-500">
                          O fechamento só acontece após sua confirmação.
                        </p>
                      </>
                    ) : (
                      <div
                        className="fixed inset-0 z-[190] flex items-end justify-center bg-black/80 backdrop-blur-md sm:items-center sm:p-4"
                        onClick={cancelCloseConfirmation}
                      >
                        <div
                          className="w-full max-w-xl overflow-hidden rounded-t-[26px] border border-slate-700 bg-[#091522] shadow-2xl shadow-black/50 sm:rounded-[26px]"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="relative overflow-hidden border-b border-slate-800 px-4 py-4 sm:px-5">
                            <div className="pointer-events-none absolute -right-10 -top-12 h-32 w-32 rounded-full bg-red-500/10 blur-3xl" />
                            <div className="relative flex items-start justify-between gap-3">
                              <div>
                                <div className="flex items-center gap-2">
                                  <span className="rounded-md border border-red-500/30 bg-red-500/10 px-2 py-1 text-[8px] font-black uppercase tracking-[0.15em] text-red-300">
                                    {accountMode === "demo" ? "CONTA DEMO" : "CONTA REAL"}
                                  </span>
                                  <span className="text-[9px] font-bold uppercase tracking-[0.15em] text-slate-500">
                                    ORDEM #{id}
                                  </span>
                                </div>
                                <h3 className="mt-2 text-xl font-black tracking-tight text-white">
                                  Confirmar fechamento
                                </h3>
                                <p className="mt-1 text-[11px] text-slate-400">
                                  Revise sua posição antes de enviar o encerramento.
                                </p>
                              </div>
                              <button
                                type="button"
                                onClick={cancelCloseConfirmation}
                                disabled={closingId === id}
                                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-slate-700 bg-slate-950 text-lg font-bold text-slate-300 transition hover:bg-slate-800"
                              >
                                ×
                              </button>
                            </div>
                          </div>

                          <div className="space-y-3 p-4 sm:p-5">
                            <div className="grid grid-cols-[1fr_auto] items-center gap-3 rounded-2xl border border-slate-800 bg-slate-950/65 p-3.5">
                              <div>
                                <p className="text-[8px] font-bold uppercase tracking-[0.15em] text-slate-500">
                                  Posição atual
                                </p>
                                <div className="mt-1 flex items-center gap-2">
                                  <span className={`text-sm font-black ${side === "buy" ? "text-emerald-300" : "text-red-300"}`}>
                                    {getSideLabel(side)}
                                  </span>
                                  <span className="text-[10px] text-slate-500">• CAFE_SACA_BRL</span>
                                </div>
                              </div>
                              <div className="text-right">
                                <p className="text-[8px] font-bold uppercase tracking-[0.15em] text-slate-500">
                                  Resultado agora
                                </p>
                                <p className={`mt-1 text-xl font-black ${
                                  plNowValue === null
                                    ? "text-slate-300"
                                    : plNowValue >= 0
                                    ? "text-emerald-300"
                                    : "text-red-300"
                                }`}>
                                  {plNowValue === null ? "—" : `${plNowValue >= 0 ? "+" : ""}${formatSignedBRL(plNowValue)}`}
                                </p>
                                {plPercent !== null && (
                                  <p className={`text-[10px] font-bold ${plPercent >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                                    {plPercent >= 0 ? "+" : ""}{formatPercent(plPercent)}
                                  </p>
                                )}
                              </div>
                            </div>

                            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                              <div className="rounded-xl border border-slate-800 bg-slate-950/45 p-3">
                                <p className="text-[8px] font-bold uppercase tracking-wider text-slate-600">Entrada</p>
                                <strong className="mt-1 block text-[12px] text-white">
                                  {entryPriceSaca > 0 ? formatBRL(entryPriceSaca, 2) : "—"}
                                </strong>
                              </div>
                              <div className="rounded-xl border border-slate-800 bg-slate-950/45 p-3">
                                <p className="text-[8px] font-bold uppercase tracking-wider text-slate-600">Preço atual</p>
                                <strong className="mt-1 block text-[12px] text-cyan-200">
                                  {currentPrice > 0 ? formatBRL(currentPrice, 2) : "—"}
                                </strong>
                              </div>
                              <div className="rounded-xl border border-slate-800 bg-slate-950/45 p-3">
                                <p className="text-[8px] font-bold uppercase tracking-wider text-slate-600">Quantidade</p>
                                <strong className="mt-1 block text-[12px] text-white">
                                  {qtySaca > 0 ? `${qtySaca.toFixed(4)} sacas` : "—"}
                                </strong>
                              </div>
                              <div className="rounded-xl border border-slate-800 bg-slate-950/45 p-3">
                                <p className="text-[8px] font-bold uppercase tracking-wider text-slate-600">Investido</p>
                                <strong className="mt-1 block text-[12px] text-white">{formatBRL(amount)}</strong>
                              </div>
                              <div className="rounded-xl border border-slate-800 bg-slate-950/45 p-3">
                                <p className="text-[8px] font-bold uppercase tracking-wider text-slate-600">Valor atual</p>
                                <strong className="mt-1 block text-[12px] text-white">{formatBRL(posCurrentValue)}</strong>
                              </div>
                              <div className="rounded-xl border border-slate-800 bg-slate-950/45 p-3">
                                <p className="text-[8px] font-bold uppercase tracking-wider text-slate-600">Aberta em</p>
                                <strong className="mt-1 block text-[10px] leading-tight text-white">
                                  {formatDateTime(order?.created_at)}
                                </strong>
                              </div>
                            </div>

                            <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 px-3 py-2.5">
                              <p className="text-[10px] leading-relaxed text-amber-100/80">
                                Ao confirmar, a posição será encerrada pelo fluxo atual do Trader. Esta ação não ocorre no primeiro toque.
                              </p>
                            </div>

                            <div className="grid grid-cols-2 gap-2 pt-1">
                              <Button
                                type="button"
                                onClick={cancelCloseConfirmation}
                                disabled={closingId === id}
                                className="h-14 rounded-2xl border border-slate-700 bg-slate-800 text-xs font-black text-white hover:bg-slate-700"
                              >
                                CONTINUAR ABERTA
                              </Button>

                              <Button
                                type="button"
                                onClick={() => handleCloseOrder(order)}
                                disabled={closingId === id || !canClose}
                                className="h-14 rounded-2xl border border-red-400/50 bg-gradient-to-b from-red-500 to-red-700 text-xs font-black text-white shadow-[0_10px_26px_rgba(239,68,68,0.22)] hover:from-red-400 hover:to-red-600"
                              >
                                {closingId === id ? "ENCERRANDO..." : "FECHAR POSIÇÃO"}
                              </Button>
                            </div>

                            <p className="text-center text-[9px] text-slate-600">
                              {accountMode === "demo"
                                ? "Conta Demo: o encerramento afeta somente a operação virtual."
                                : "Conta Real: confirme somente se deseja encerrar esta posição."}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {!canClose && toNumber(userSettings?.min_close_time_minutes, 0) > 0 && (
                      <p className="mt-2 text-center text-[11px] font-bold text-amber-300">
                        Aguarde {formatRemaining(remainingMs)} para encerrar.
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}

            {isOpen && protectionId === id && (
              <div
                className="fixed inset-0 z-[195] flex items-end justify-center bg-black/75 backdrop-blur-sm sm:items-center sm:p-4"
                onClick={closeProtectionEditor}
              >
                <div
                  className="w-full max-w-lg overflow-hidden rounded-t-3xl border border-slate-800 bg-slate-900 shadow-2xl shadow-black/40 sm:rounded-3xl"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="border-b border-slate-800 px-5 py-4">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-[9px] font-semibold uppercase tracking-[0.18em] text-cyan-300">
                          {accountMode === "demo" ? "CONTA DEMO" : "CONTA REAL"} • ORDEM #{id}
                        </p>
                        <h3 className="mt-1 text-lg font-black text-white">Stop Loss e Take Profit</h3>
                      </div>
                      <button
                        type="button"
                        onClick={closeProtectionEditor}
                        disabled={savingProtection}
                        className="flex h-9 w-9 items-center justify-center rounded-full border border-slate-700 bg-slate-950 text-lg font-bold text-slate-300"
                      >
                        ×
                      </button>
                    </div>
                  </div>

                  <div className="space-y-4 p-5">
                    <div className="rounded-2xl border border-slate-800 bg-slate-950/60 p-4 text-xs">
                      <div className="flex justify-between gap-3">
                        <span className="text-slate-400">Posição</span>
                        <strong className={side === "buy" ? "text-emerald-300" : "text-cyan-300"}>{getSideLabel(side)}</strong>
                      </div>
                      <div className="mt-2 flex justify-between gap-3">
                        <span className="text-slate-400">Preço atual</span>
                        <strong className="text-white">{currentPrice > 0 ? formatBRL(currentPrice, 4) : "—"}</strong>
                      </div>
                    </div>

                    <div>
                      <label className="mb-1.5 block text-xs font-bold text-red-300">STOP LOSS</label>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={protectionSL}
                        onChange={(e) => setProtectionSL(e.target.value)}
                        placeholder={side === "buy" ? "Preço abaixo do atual" : "Preço acima do atual"}
                        className="h-12 w-full rounded-xl border border-red-500/20 bg-slate-950 px-3 text-sm font-bold text-white outline-none focus:border-red-400/50"
                      />
                      <p className="mt-1 text-[10px] text-slate-500">
                        {side === "buy" ? "Na compra, encerra se o preço cair até este nível." : "Na venda, encerra se o preço subir até este nível."}
                      </p>
                    </div>

                    <div>
                      <label className="mb-1.5 block text-xs font-bold text-emerald-300">TAKE PROFIT</label>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={protectionTP}
                        onChange={(e) => setProtectionTP(e.target.value)}
                        placeholder={side === "buy" ? "Preço acima do atual" : "Preço abaixo do atual"}
                        className="h-12 w-full rounded-xl border border-emerald-500/20 bg-slate-950 px-3 text-sm font-bold text-white outline-none focus:border-emerald-400/50"
                      />
                      <p className="mt-1 text-[10px] text-slate-500">
                        {side === "buy" ? "Na compra, realiza o lucro se o preço subir até este nível." : "Na venda, realiza o lucro se o preço cair até este nível."}
                      </p>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <Button
                        type="button"
                        onClick={() => {
                          setProtectionSL("");
                          setProtectionTP("");
                        }}
                        disabled={savingProtection}
                        className="h-12 rounded-xl bg-slate-800 text-xs font-black text-white hover:bg-slate-700"
                      >
                        LIMPAR
                      </Button>
                      <Button
                        type="button"
                        onClick={() => saveProtection(order)}
                        disabled={savingProtection}
                        className="h-12 rounded-xl bg-cyan-600 text-xs font-black text-white hover:bg-cyan-500"
                      >
                        {savingProtection ? "SALVANDO..." : "SALVAR PROTEÇÃO"}
                      </Button>
                    </div>

                    <p className="text-center text-[10px] text-slate-500">
                      Deixe um campo vazio se não quiser usar aquela proteção.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}